import { AbiCoder, getBytes, Interface, keccak256, toBeHex, ZeroAddress } from 'ethers'

import { SafeMultisigTransactionResponse } from '@safe-global/types-kit'

import { EIP7702Auth } from '../../consts/7702'
import { SINGLETON } from '../../consts/deploy'
import { AccountId } from '../../interfaces/account'
import { Key } from '../../interfaces/keystore'
import { SwapAndBridgeQuote, SwapAndBridgeSendTxRequest } from '../../interfaces/swapAndBridge'
import { PaymasterService } from '../erc7677/types'
import { UserOperation } from '../userOperation/types'
import { AccountOpStatus, Call, CallTuple } from './types'

// This is an abstract representation of the gas fee payment
// 1) it cannot contain details about maxFeePerGas/baseFee because some networks might not be aware of EIP-1559; it only cares about total amount
// 2) it cannot contain info about the mechanism of payment (from EOA but on smart account, pure EOA paying it's fee directly, 4337 paymaster, 4337 direct, relayer, etc.)
// This info can be inferred when needed from the account type and whether we're running in 4337 mode or not
// 3) isGasTank and isERC4337 can both be true
// 4) whether those values are sane will be checked in an additional function (currently `canBroadcast`); for example, this function is meant to ensure that in case of an EOA, the fee is always paid in native
export interface GasFeePayment {
  isGasTank: boolean
  paidBy: string
  /**
   * The account may be controlled by multiple keys. In this case, the user should
   * be able to choose which key to use.
   */
  paidByKeyType: Key['type'] | null
  inToken: string
  // optional, because older versions of the extension did not have this stored locally
  feeTokenChainId?: bigint
  amount: bigint
  /**
   * ZKX: the most the broadcast can require in the fee token, see
   * `calculateMaxBroadcastCost`. Above `amount` when the account broadcasts
   * itself. Optional: fee payments stored by older versions do not have it.
   */
  maxCost?: bigint
  simulatedGasLimit: bigint
  isCustomGasLimit?: boolean
  gasPrice: bigint
  broadcastOption: string
  maxPriorityFeePerGas?: bigint
  isSponsored?: boolean
}

// Equivalent to ERC-4337 UserOp, but more universal than it since a AccountOp can be transformed to
// a UserOp, or to a direct EOA transaction, or relayed through the Ambire relayer
// it is more precisely defined than a UserOp though - UserOp just has calldata and this has individual `calls`
export interface AccountOp {
  id: string
  accountAddr: string
  chainId: bigint
  // this may not be defined, in case the user has not picked a key yet
  // also, this represents the last signer (if it's only 1, the only signer,
  // but in case of multi-sig accounts, the last signer will be saved)
  signingKeyAddr: Key['addr'] | null
  signingKeyType: Key['type'] | null
  // this may not be set in case we haven't set it yet
  // this is a number and not a bigint because of ethers (it uses number for nonces)
  nonce: bigint | null
  // the EOA nonce used for raw transaction broadcast, if applicable
  eoaNonce?: bigint | null
  // @TODO: nonce namespace? it is dependent on gasFeePayment
  calls: Call[]
  // the feeCall is an extra call we add manually when there's a
  // relayer/paymaster transaction so that the relayer/paymaster
  // can authorize the payment
  feeCall?: Call
  // the activator call is for cases where we want to activate the EntryPoint
  // it existed previously in the UserOperation type but now it is no longer
  // limited to it as we can broadcast none ERC-4337 txn with an activatorCall
  activatorCall?: Call
  gasLimit: number | null
  signature: string | null
  gasFeePayment: GasFeePayment | null
  txnId?: string
  status?: AccountOpStatus
  // in the case of ERC-4337, we need an UserOperation structure for the AccountOp
  asUserOperation?: UserOperation
  // multiple signers use case
  // all the signers that are to sign the transaction
  signers?: { addr: Key['addr']; type: Key['type'] }[]
  // who are the signers that already signed this txn
  signed?: string[]
  safeTx?: SafeMultisigTransactionResponse
  // all kinds of custom accountOp properties that are needed in specific cases
  meta?: {
    // pass the entry point authorization signature for the deploy 4337 txn
    entryPointAuthorization?: string
    paymasterService?: PaymasterService
    swapTxn?: SwapAndBridgeSendTxRequest
    /**
     * Marks a txn the wallet did NOT broadcast itself as the source leg of a
     * swap. A gasless swap is broadcast by the relayer, so the wallet only ever
     * meets it again through the generic transfer scanner, which records it as
     * a plain outgoing transfer. Without this flag the activity row has nothing
     * to tell it apart from a send: `swapTxn` is absent (there was no AccountOp
     * to attach it to) and the incoming leg lands on another chain.
     */
    isRelayerBroadcastSwap?: boolean
    quote?: SwapAndBridgeQuote
    walletSendCallsVersion?: string
    delegation?: EIP7702Auth
    setDelegation?: boolean
    /** Used to determine if the account op is up-to-date with the latest quote */
    fromQuoteId?: string
    /** Used to enable the gas tank if the user is topping up */
    topUpAmount?: bigint
    /**
     * Estimated USD value of a transfer, stamped at build time so a large-transfer
     * re-auth gate (B6) can read a value bound to THIS op instead of the live
     * transfer form (which is reset on navigation before broadcast). `null` means
     * the token had no USD price — the gate treats that as fail-safe (require
     * confirmation). Absent on non-transfer ops.
     */
    transferValueInUsd?: number | null
    /** Allows transfer.ts-owned MAX flows to reserve the fee from the transferred token. */
    allowTransferFeeTokenSelfReserve?: boolean
    /** Used to enable swap&bridge sponsorship */
    swapSponsorship?: {
      swapFeeInUsd: number
      nativePrice: number
      fromTokenPriceInUsd: number
      fromTokenDecimals: number
    }
    speedUp?: {
      enabled: boolean
    }
  }
  flags?: {
    hideActivityBanner?: boolean
    /**
     * The op's nonce is spent onchain but no receipt could ever be read for it, so
     * we know it is over without knowing how it ended. Set by the activity
     * controller's nonce reconciliation; it turns the `UnknownButPastNonce` status
     * from "replaced by fee" into "could not confirm" in the UI, which is the
     * honest reading in this case.
     */
    unresolvedAfterNonceSpent?: boolean
  }
}

/**
 * A Safe "on-chain rejection" is a transaction that only exists to occupy a pending Safe
 * nonce, replacing whatever real transaction was queued at it. Safe (the protocol) has no
 * dedicated rejection type - it's just a no-op call: a single call with no value and no data,
 * sent either to the zero address or to the Safe itself, executed as a regular call (not a
 * delegate call).
 *
 * This is the source of truth for that check, sharable by anything that has the raw call data
 * (a fetched Safe transaction, a built `AccountOp`, etc.) without needing a full `AccountOp`.
 *
 * @param calls the calls of the transaction, e.g. `accountOp.calls`
 * @param accountAddr the Safe account the transaction belongs to
 * @param operation the Safe `operation` value of the transaction (0 = Call, 1 = DelegateCall);
 * pass the `operation` from the fetched Safe transaction, or omit it for a transaction built
 * locally, which is always a plain Call
 */
export function isSafeRejectionCall(
  calls: Call[],
  accountAddr: string,
  operation: number = 0
): boolean {
  if (calls.length !== 1) return false
  if (!calls[0]) return false
  const [call] = calls

  return (
    operation === 0 &&
    !!call.to &&
    (call.to.toLowerCase() === ZeroAddress ||
      call.to.toLowerCase() === accountAddr.toLowerCase()) &&
    call.value === 0n &&
    call.data === '0x'
  )
}

/**
 * If we want to deploy a contract, the to field of Call will actually
 * be empty (undefined). In order to simulate it in a transaction or
 * perform it using a smart account, we need to transform the call to
 * a call to the singleton
 *
 * @param call
 * @returns Call
 */
export function toSingletonCall(call: Call): Call {
  if (call.to) return call

  const singletonABI = [
    {
      inputs: [
        { internalType: 'bytes', name: '_initCode', type: 'bytes' },
        { internalType: 'bytes32', name: '_salt', type: 'bytes32' }
      ],
      name: 'deploy',
      outputs: [{ internalType: 'address payable', name: 'createdContract', type: 'address' }],
      stateMutability: 'nonpayable',
      type: 'function'
    }
  ]
  const singletonInterface = new Interface(singletonABI)
  return {
    to: SINGLETON,
    value: call.value,
    data: singletonInterface.encodeFunctionData('deploy', [call.data, toBeHex(0, 32)])
  }
}

export function callToTuple(call: Call): CallTuple {
  return [call.to, call.value.toString(), call.data]
}

export function canBroadcast(op: AccountOp, accountIsEOA: boolean): boolean {
  if (op.signingKeyAddr === null) throw new Error('missing signingKeyAddr')
  if (op.signature === null) throw new Error('missing signature')
  if (op.gasFeePayment === null) throw new Error('missing gasFeePayment')
  if (op.gasLimit === null) throw new Error('missing gasLimit')
  if (op.nonce === null) throw new Error('missing nonce')
  if (accountIsEOA) {
    if (op.gasFeePayment.isGasTank)
      throw new Error('gas fee payment with gas tank cannot be used with an EOA')
    if (op.gasFeePayment.inToken !== '0x0000000000000000000000000000000000000000')
      throw new Error('gas fee payment needs to be in the native asset')
    if (op.gasFeePayment.paidBy !== op.accountAddr)
      throw new Error('gas fee payment cannot be paid by anyone other than the EOA that signed it')
  }
  return true
}

export function getSignableCalls(op: AccountOp): CallTuple[] {
  const callsToSign = op.calls.map(toSingletonCall).map(callToTuple)
  if (op.activatorCall) callsToSign.push(callToTuple(op.activatorCall))
  if (op.feeCall) callsToSign.push(callToTuple(op.feeCall))
  return callsToSign
}

export function getSignableHash(
  addr: AccountId,
  chainId: bigint,
  nonce: bigint,
  calls: CallTuple[]
): Uint8Array {
  const abiCoder = new AbiCoder()
  return getBytes(
    keccak256(
      abiCoder.encode(
        ['address', 'uint', 'uint', 'tuple(address, uint, bytes)[]'],
        [addr, chainId, nonce, calls]
      )
    )
  )
}

/**
 * This function returns the hash as a Uint8Array instead of string
 * and the reason for this is the implementation that follows:
 *
 * const hash = accountOpSignableHash(op); // get the hash
 * const signature = await wallet.signMessage(hash)
 *
 * The signMessage method is an ethers method. It checks whether
 * the hash is a string or not. If it's a string, it calls
 * ethers.toUtf8Bytes to it, completing ignoring that the string
 * might actually be abi-encoded (like in our case).
 *
 * Applying ethers.toUtf8Bytes to a string is only correct if the
 * string is... a utf8 string. In our case, IT IS NOT.
 * That's why we need to wrap in with ethers.getBytes to prevent
 * the sign message from breaking it.
 *
 * If despite everything you wish to return a string instead of a Uint8Array,
 * you have to wrap the hash with ethers.getBytes each time before passing it
 * to signMessage. Also, the reverse method of ethers.getBytes is ethers.hexlify
 * if you need to transform it back.
 *
 * @param op AccountOp
 * @returns Uint8Array
 */
export function accountOpSignableHash(op: AccountOp, chainId: bigint): Uint8Array {
  return getSignableHash(op.accountAddr, chainId, op.nonce ?? 0n, getSignableCalls(op))
}

export const areAccountOpsEqual = (ops1: AccountOp[], ops2: AccountOp[]) => {
  if (ops1.length !== ops2.length) return false

  const ops2Ids = new Set(ops2.map((op) => op.id))

  for (const op1 of ops1) {
    if (!ops2Ids.has(op1.id)) return false

    if (op1.nonce !== ops2.find((op2) => op2.id === op1.id)?.nonce) return false
  }
  return true
}

export function haveCallsChanged(callsOne: AccountOp['calls'], callsTwo: AccountOp['calls']) {
  const lengthDiff = callsOne.length !== callsTwo.length
  if (lengthDiff) return true

  // if some of their properties differ, then calls have changed
  for (let i = 0; i < callsOne.length; i++) {
    const callOne = callsOne[i]!
    const callTwo = callsTwo[i]!
    if (
      callOne.to !== callTwo?.to ||
      callOne.data !== callTwo?.data ||
      callOne.value !== callTwo?.value ||
      callOne.id !== callTwo?.id
    ) {
      return true
    }
  }
  return false
}

export function haveAccountOpsChanged(accountOpsOne: AccountOp[], accountOpsTwo: AccountOp[]) {
  const lengthDiff = accountOpsOne.length !== accountOpsTwo.length
  if (lengthDiff) return true

  for (let i = 0; i < accountOpsOne.length; i++) {
    const oneOp = accountOpsOne[i]!
    const twoOp = accountOpsTwo[i]!
    if (haveCallsChanged(oneOp.calls, twoOp.calls)) return true
  }

  return false
}
